import { requestPostMarkupLeave } from "@/components/Collaboration/markup/post-markup-leave-guards";
/* eslint-disable unicorn/no-negated-condition */
/* eslint-disable unicorn/explicit-length-check */
import React, { useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { createGlobalStyle } from "styled-components";
import { Modal } from "@/components/modal/modal";
import cloneDeep from "lodash/cloneDeep";

//components
import { FullPageLoading } from "@/components/ui/full-page-loading";
import PostForm from "components/Posts/PostForm/PostForm";

//actions
import { hideModal, showConfirmModal } from "actions/modal";
import { checkInstagramPostOverlapping, savePostForm } from "actions/postForm";

//selectors
import { getLoading, getUser } from "selectors/commonSelectors";
import { getFirstForm, PostFormRootState } from "selectors/postFormSelectors";

//utils
import { closeCreatePostModal } from "../create-modal.model";
import { closePostForm } from "../post-form.model";
import { usePostForm } from "components/Posts/PostForm/use-post-form";
import { POST_STATES, PostStatesType } from "constants/PostsView";
import { useTrackEvent } from "libs/analytics";
import { reloadAssetCurrentPage, reloadTags } from "actions/mediaLibrary";

import { plannerPageScheduleClusterPost } from "pages/planner/__redux-model";
import {
  CREATE_POST_YT,
  CREATE_SINGLE_POST,
  CREATE_POST_LI,
} from "constants/Events";
import { Post } from "../types";
import { AccountType } from "shared/types/accounts";
import { PostMediaFile } from "../../media-library/types";
import { createPostNormalizer } from "./create-post-normalizer";
import { BulkTabContext } from "../../../components/Posts/PostForm/types";
import { getPostedState } from "../../../utils/posts";
import { useCollaborationGeneralChannel } from "../../../components/Collaboration/useCollaborationGeneralChannel";

const EMPTY_ASSETS: PostMediaFile[] = [];

export const CreatePostModal = ({
  post,
  accounts,
  caption,
  assets = EMPTY_ASSETS,
  accountEditingIsDisabled,
  isHidden,
  bulkTab,
  formTitle,
}: CreatePostModalParams) => {
  const dispatch = useDispatch();
  const loading = useSelector(getLoading);
  const form = useSelector((state: PostFormRootState) =>
    getFirstForm(state, bulkTab?.formId)
  );
  const [isFormValueChanged, setIsFormValueChanged] = useState(false);
  const trackEvent = useTrackEvent();
  const postObject = createPostNormalizer.makePostForCreate({
    post,
    caption,
    assets,
    accounts,
  });

  const trackEventYT = (post: Post) => {
    const isVideoPostWithYouTube =
      post.type === "video" &&
      post.accounts?.some((el) => el.platformType === "YT");

    if (isVideoPostWithYouTube && post.meta?.format?.duration) {
      // eslint-disable-next-line unicorn/require-number-to-fixed-digits-argument
      const duration = +(+post.meta.format.duration).toFixed();
      if (duration <= 60) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "< 1 min" },
        });
      } else if (duration <= 900) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "1 to 15 min" },
        });
      } else if (duration > 900) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "over 15 min" },
        });
      }
    }
  };

  const trackEventLI = (post: Post) => {
    if (post._id === "NEW_POST") {
      // eslint-disable-next-line unicorn/no-for-each
      post.accounts?.forEach((account) => {
        if (account.platformType === "LI") {
          trackEvent({
            eventName: CREATE_POST_LI,
            metadata: {
              pageType: account.linkedinUrn?.includes("person")
                ? "Personal Page"
                : "Company Page",
            },
          });
        }
      });
    }
  };

  const postForm = usePostForm(
    postObject,
    {
      async onSave(data: { post: Post }) {
        const { post: postForm } = data;
        await new Promise((resolve) =>
          dispatch(
            checkInstagramPostOverlapping({
              postId: postForm._id,
              accounts: postForm.accounts,
              isNow: postForm.isNow,
              when: postForm.when,
              post: postForm,
              done: resolve,
            })
          )
        );

        trackEvent({ eventName: CREATE_SINGLE_POST });
        trackEventYT(postForm);
        trackEventLI(postForm);
        dispatch(savePostForm(data));
      },
      onSuccess() {
        closeModal();
      },
    },
    bulkTab
  );

  const cancelWithoutConfirm = () => {
    dispatch(closeCreatePostModal());
    dispatch(reloadAssetCurrentPage());
    dispatch(reloadTags());
  };

  function handleCancel() {
    dispatch(
      showConfirmModal({
        title: "Discard post?",
        message: `Are you sure you want to discard this post? Any unsaved work will be lost.`,
        confirmText: "Keep editing",
        cancelText: "Discard post",
        confirmButtonProps: {
          variant: "blue-secondary",
        },
        handleConfirm() {
          // continue editing, so hide only confirm modal
          dispatch(hideModal(false, "CONFIRM_MODAL"));
        },
        handleCancel() {
          // should hide all modals present because this is lose changes option
          dispatch(hideModal(true));
          closeModal();
        },
      })
    );
  }

  function closeModal() {
    dispatch(closeCreatePostModal());
    dispatch(reloadAssetCurrentPage());
    dispatch(reloadTags());
  }
  const postFormJsx = form && (
    <PostForm
      {...postForm}
      setIsFormValueChanged={setIsFormValueChanged}
      onCancel={isFormValueChanged ? handleCancel : cancelWithoutConfirm}
      accountEditingIsDisabled={accountEditingIsDisabled}
      bulkTab={bulkTab}
      formTitle={formTitle}
    />
  );

  if (bulkTab) {
    return postFormJsx;
  }

  return (
    <PostModal
      title="Create Post"
      onCancel={isFormValueChanged ? handleCancel : cancelWithoutConfirm}
      isLoading={loading}
      isHidden={isHidden}
    >
      {postFormJsx}
    </PostModal>
  );
};

export function EditPostModal({
  post,
  accountEditingIsDisabled,
  isHidden,
  bulkTab,
  formTitle,
}: {
  post: Post;
  accountEditingIsDisabled: boolean;
  isHidden: boolean;
  bulkTab?: BulkTabContext;
  formTitle?: string;
}) {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const [isFormValueChanged, setIsFormValueChanged] = useState(false);

  useCollaborationGeneralChannel(user);

  let postForm = usePostForm(
    post,
    {
      onSuccess() {},
      onSave(data, saveAction) {
        const isDraft = data.newPostState === POST_STATES.DRAFT;
        const hasMultipleAccs = data.post.accounts.length >= 2;
        if (bulkTab) {
          bulkTab.onSave?.(data, saveAction);
          return;
        }
        // if saving modal not from planner
        // or user saves draft
        // or single account
        if (!data.plannerAccount || isDraft || !hasMultipleAccs) {
          dispatch(closePostForm(data));
        } else {
          handlePlannerMultipost(data);
        }
      },
    },
    bulkTab
  ) as ReturnType<typeof usePostForm> & {
    allDisabled?: boolean;
    isSummary?: boolean;
  };

  function handleCancelEdit() {
    dispatch(hideModal(false, "CONFIRM_MODAL"));
    dispatch(closePostForm());
    dispatch(reloadAssetCurrentPage());
    dispatch(reloadTags());
  }

  const cancelWithoutConfirm = () => {
    dispatch(closePostForm());
    dispatch(reloadAssetCurrentPage());
    dispatch(reloadTags());
  };

  function handleCancelClick() {
    if (!post.past) {
      dispatch(
        showConfirmModal({
          title: "Discard post?",
          message: `Are you sure you want to discard this post? Any unsaved work will be lost.`,
          confirmText: "Keep editing",
          cancelText: "Discard post",
          confirmButtonProps: {
            variant: "blue-secondary",
          },
          handleConfirm() {
            // continue editing, so hide only confirm modal
            dispatch(hideModal(false, "CONFIRM_MODAL"));
          },
          handleCancel() {
            handleCancelEdit();
          },
        })
      );
    } else {
      handleCancelEdit();
    }
  }

  // TODO: move to /pages/planner/model.ts
  const savePostFromPlanner = (data: PlannerPostData) => {
    const { plannerAccount } = data;
    dispatch(hideModal(false, "CONFIRM_MODAL"));
    const scheduledPostData = cloneDeep(data);
    const initial = cloneDeep(data);
    // scheduled post will be created as new post
    scheduledPostData.post.accounts = [plannerAccount];
    scheduledPostData.post.accountIds = [plannerAccount._id];
    scheduledPostData.post.newPost = true;
    // remaining clusterpost without account that was selected in planner
    // will be updated
    initial.newPostState = initial.originalPostState;
    initial.post.accounts = initial.post.accounts?.filter(
      (acc) => acc._id !== plannerAccount._id
    );
    initial.post.accountIds = initial.post.accountIds.filter(
      (accId) => accId !== plannerAccount._id
    );
    // save initial changes
    dispatch(
      plannerPageScheduleClusterPost({
        initialPost: initial,
        scheduledPost: scheduledPostData,
      })
    );
  };

  // TODO: move to /pages/planner/model.ts
  const handlePlannerMultipost = (data: PlannerPostData) => {
    // show modal, alerting that only 1 account will be scheduled/queued
    // the one selected in planner
    const [title, action] = {
      [POST_STATES.QUEUED]: ["Queue Post", "queued"],
      [POST_STATES.SCHEDULED]: ["Schedule Post", "scheduled"],
    }[data.newPostState as "QUEUED" | "SCHEDULED"];
    // show modal
    dispatch(
      showConfirmModal({
        title,
        message: `This draft post will be ${action} for the ${data.plannerAccount.login} account only.`,
        confirmType: "primary_outline",
        confirmText: "OK",
        cancelType: "alert",
        cancelText: "Cancel",
        handleConfirm() {
          savePostFromPlanner(data);
        },
        handleCancel() {
          dispatch(hideModal(true));
        },
      })
    );
  };

  if (getPostedState(post) === "failed") {
    postForm = {
      ...postForm,
      post: {
        ...postForm.post,
        past: false,
      },
    };
  } else if (getPostedState(post) === "success") {
    postForm = {
      ...postForm,
      post: {
        ...postForm.post,
        past: true,
      },
      allDisabled: true,
      errors: undefined,
      isSummary: true,
    };
  }
  const formJsx = (
    <PostForm
      {...postForm}
      onCancel={
        bulkTab?.onCancel
          ? bulkTab.onCancel
          : () =>
              requestPostMarkupLeave(
                post._id,
                isFormValueChanged ? handleCancelClick : cancelWithoutConfirm
              )
      }
      setIsFormValueChanged={(value) => {
        setIsFormValueChanged(value);
        bulkTab?.onFormValueChanged?.(value);
      }}
      accountEditingIsDisabled={accountEditingIsDisabled}
      bulkTab={bulkTab}
      formTitle={formTitle}
    />
  );

  if (bulkTab) {
    return formJsx;
  }

  return (
    <PostModal
      isHidden={isHidden}
      title={
        getPostedState(post) === "success"
          ? "Post Summary"
          : getPostedState(post) === "failed"
            ? "Post Again"
            : "Edit Post"
      }
      onCancel={() =>
        requestPostMarkupLeave(
          post._id,
          isFormValueChanged ? handleCancelClick : cancelWithoutConfirm
        )
      }
      isLoading={false}
    >
      {formJsx}
    </PostModal>
  );
}

export function PostModal(props: {
  title: string;
  isLoading: boolean;
  isHidden?: boolean;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [eventClickStart] = useState<{ target: HTMLElement | null }>({
    target: null,
  });
  const handleMouseDown = (event: Event) => {
    eventClickStart.target = event.target as HTMLElement;
  };

  const handleMouseUp = (eventClickEnd: Event) => {
    if (
      bodyRef.current?.contains(eventClickStart.target) &&
      !bodyRef.current?.contains(eventClickEnd.target as HTMLElement)
    ) {
      eventClickEnd.stopPropagation();
      eventClickEnd.preventDefault();
    }
  };

  useLayoutEffect(() => {
    const modal = document.querySelector('[role="dialog"] .in.modal');
    if (modal) {
      modal.addEventListener("mousedown", handleMouseDown);
      modal.addEventListener("mouseup", handleMouseUp);
      modal.addEventListener("click", handleMouseUp);
    }
    return () => {
      if (modal) {
        modal.removeEventListener("mousedown", handleMouseDown);
        modal.removeEventListener("mouseup", handleMouseUp);
        modal.removeEventListener("click", handleMouseUp);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRef.current]);

  return (
    <CreatePostModalRoot
      show={!props.isHidden}
      onHide={props.onCancel}
      animation={false}
      bsStyle="giselle"
      modal={false}
      backdrop="static"
      className="h-[calc(100vh-64px)] max-h-[calc(100vh-64px)] w-[calc(100vw-32px)] min-w-0 max-w-[1760px] overflow-hidden"
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="create-post-modal"
        ref={bodyRef}
      >
        <ChildrenModalFixScrollStyles />
        <Modal.Header className="flex-none" closeButton>
          <Modal.Title>{props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="flex min-h-0 flex-1 overflow-hidden">
          {props.isLoading && <FullPageLoading />}
          {props.children}
        </Modal.Body>
      </div>
    </CreatePostModalRoot>
  );
}

const CreatePostModalRoot = styled(Modal)<{ bsStyle?: string }>`
  .modal-dialog,
  .modal-content {
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }
`;

const ChildrenModalFixScrollStyles = createGlobalStyle`
  .modal {
    overflow-y: auto;
  }
`;

interface CreatePostModalParams {
  post: Partial<Post>;
  accounts?: AccountType[];
  caption?: string;
  assets?: PostMediaFile[];
  accountEditingIsDisabled?: boolean;
  isHidden?: boolean;
  bulkTab?: BulkTabContext;
  formTitle?: string;
}

export interface PlannerPostData {
  post: Post;
  plannerAccount: AccountType;
  newPostState: PostStatesType;
  originalPostState: PostStatesType;
}
